<template>
    <div class="reset-page">
        <div class="top-nav">
            <button class="back-btn" @click="handleBack">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="mr-1">
                    <path d="M15 18L9 12L15 6" stroke="#ff466a" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
                Back
            </button>
            <button class="signin-top-btn" @click="$router.push('/signin')">Log In</button>
        </div>

        <div v-if="currentStep !== 4" class="reset-container">
            <div class="icon-wrap">
                <div class="lock-icon" :class="{ success: currentStep === 4 }">
                    <div v-if="currentStep !== 4" class="lock-shackle"></div>
                    <div v-if="currentStep !== 4" class="lock-body">
                        <div class="keyhole"></div>
                        <div class="badge">?</div>
                    </div>
                </div>
            </div>

            <template v-if="currentStep === 1">
                <h2 class="step-title">Forgot your password?</h2>
                <p class="step-subtitle">Enter your phone number or email to reset your password.</p>

                <div class="tabs">
                    <div
                        class="tab"
                        :class="{ active: activeTab === 'email' }"
                        @click="
                            activeTab = 'email'
                            errors.step1 = false
                        ">
                        Email
                    </div>
                    <div
                        class="tab"
                        :class="{ active: activeTab === 'phone' }"
                        @click="
                            activeTab = 'phone'
                            errors.step1 = false
                        ">
                        Phone
                    </div>
                </div>

                <div v-if="activeTab === 'email'" class="input-container" :class="{ 'error-border': errors.step1 && !isEmailValid }">
                    <svg class="input-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M4 7L10.2 11.65C11.2667 12.45 12.7333 12.45 13.8 11.65L20 7" stroke="#C5C7CF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                        <rect x="3" y="5" width="18" height="14" rx="2" stroke="#C5C7CF" stroke-width="2" stroke-linecap="round" />
                    </svg>
                    <input type="email" placeholder="Email" v-model="formData.email" @input="errors.step1 = false" />
                </div>

                <div v-if="activeTab === 'phone'" class="phone-row">
                    <div class="phone-selector-box" :class="{ 'error-border': errors.step1 && !isPhoneValid }">
                        <PhoneAreaCodeSelect v-model="selectedPhoneAreaCode" @error-change="phoneAreaCodeError = $event" />
                    </div>
                    <div class="phone-input-box input-container" :class="{ 'error-border': errors.step1 && !isPhoneValid }">
                        <input type="tel" placeholder="Phone number" v-model="formData.phone" @input="errors.step1 = false" />
                    </div>
                </div>

                <div v-if="phoneAreaCodeError && activeTab === 'phone'" class="helper-text">{{ phoneAreaCodeError }}</div>
                <div v-if="errors.step1" class="error-message">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="12" r="10" fill="#FF5A5F" />
                        <rect x="11" y="7" width="2" height="6" rx="1" fill="white" />
                        <rect x="11" y="15" width="2" height="2" rx="1" fill="white" />
                    </svg>
                    <span>{{ activeTab === 'email' ? 'Enter a valid email address.' : 'Enter a valid phone number.' }}</span>
                </div>

                <button class="primary-btn" :class="{ active: isStep1Valid }" :disabled="!isStep1Valid || isLoading" @click="sendResetCode">
                    <span v-if="!isLoading">Send code</span>
                    <div v-else class="loader"></div>
                </button>
            </template>

            <template v-if="currentStep === 2">
                <h2 class="step-title">Enter the 6-digit code</h2>
                <p class="step-subtitle">Check your messages (text or email) for the code we sent.</p>

                <div class="code-inputs" :class="{ 'error-code-group': errors.step2 }">
                    <input
                        v-for="(digit, index) in formData.verificationCode"
                        :key="index"
                        type="text"
                        inputmode="numeric"
                        maxlength="1"
                        v-model="formData.verificationCode[index]"
                        ref="codeInputs"
                        @input="onCodeInput($event, index)"
                        @keydown.delete="onCodeDelete($event, index)"
                        @paste="onCodePaste"
                        class="code-box"
                        :class="{ 'error-border': errors.step2 }" />
                </div>
                <div v-if="errors.step2" class="error-message error-inline">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="12" r="10" fill="#FF5A5F" />
                        <rect x="11" y="7" width="2" height="6" rx="1" fill="white" />
                        <rect x="11" y="15" width="2" height="2" rx="1" fill="white" />
                    </svg>
                    <span>Incorrect code. Please check and try again.</span>
                </div>

                <button class="primary-btn" :class="{ active: isStep2Valid }" :disabled="!isStep2Valid || isLoading" @click="verifyResetCode">
                    <span v-if="!isLoading">Verify</span>
                    <div v-else class="loader"></div>
                </button>
            </template>

            <template v-if="currentStep === 3">
                <h2 class="step-title">Create a new password</h2>
                <p class="step-subtitle">Include letters and numbers for stronger security.</p>

                <div class="input-container mb-4" :class="{ 'error-border': errors.step3 && !isPasswordStrong }">
                    <input :type="showPwd1 ? 'text' : 'password'" placeholder="Password" v-model="formData.password" @input="errors.step3 = false" />
                    <img :src="showPwd1 ? require('@/assets/eye_open.png') : require('@/assets/eye.png')" class="eye-icon" @click="showPwd1 = !showPwd1" alt="Toggle password visibility" />
                </div>
                <div class="input-container" :class="{ 'error-border': errors.step3 && formData.password !== formData.rePassword }">
                    <input :type="showPwd2 ? 'text' : 'password'" placeholder="Re-enter password" v-model="formData.rePassword" @input="errors.step3 = false" />
                    <img :src="showPwd2 ? require('@/assets/eye_open.png') : require('@/assets/eye.png')" class="eye-icon" @click="showPwd2 = !showPwd2" alt="Toggle password visibility" />
                </div>
                <div v-if="errors.step3" class="error-message">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="12" r="10" fill="#FF5A5F" />
                        <rect x="11" y="7" width="2" height="6" rx="1" fill="white" />
                        <rect x="11" y="15" width="2" height="2" rx="1" fill="white" />
                    </svg>
                    <span>{{ step3ErrorMsg }}</span>
                </div>

                <button class="primary-btn" :class="{ active: isStep3Valid }" :disabled="!isStep3Valid || isLoading" @click="resetPassword">
                    <span v-if="!isLoading">Reset Password</span>
                    <div v-else class="loader"></div>
                </button>
            </template>
        </div>

        <div v-else class="success-container">
            <div class="success-icon">
                <div class="envelope">
                    <div class="envelope-check"></div>
                </div>
            </div>
            <h2 class="step-title">Your password has been reset.</h2>
            <p class="step-subtitle">You can now sign in with your new password to continue.</p>
            <button class="primary-btn active success-btn" @click="$router.push('/signin')">Return to Sign In</button>
        </div>

        <Toast v-if="toast.visible" :message="toast.message" :type="toast.type" @close="toast.visible = false" />
    </div>
</template>

<script>
import Toast from './Toast.vue'
import PhoneAreaCodeSelect from './PhoneAreaCodeSelect.vue'
import { buildApiUrl } from '@/config/api'

const AUTH_CODE_SCENE_RESET_PASSWORD = 'RESET_PASSWORD'

export default {
    name: 'ResetPassword',
    components: {
        Toast,
        PhoneAreaCodeSelect
    },
    data() {
        return {
            currentStep: 1,
            activeTab: 'email',
            formData: {
                email: '',
                phone: '',
                verificationCode: ['', '', '', '', '', ''],
                password: '',
                rePassword: ''
            },
            selectedPhoneAreaCode: {
                name: 'United States, Canada',
                code: '+1',
                countryCode: 'US',
                flagEmoji: ''
            },
            phoneAreaCodeError: '',
            verifyTicket: '',
            showPwd1: false,
            showPwd2: false,
            isLoading: false,
            errors: {
                step1: false,
                step2: false,
                step3: false
            },
            toast: {
                visible: false,
                message: '',
                type: 'info'
            }
        }
    },
    computed: {
        isEmailValid() {
            return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.formData.email)
        },
        isPhoneValid() {
            return /^\d{10,15}$/.test(this.formData.phone)
        },
        isStep1Valid() {
            return this.activeTab === 'email' ? this.isEmailValid : this.isPhoneValid
        },
        isStep2Valid() {
            return this.formData.verificationCode.every(code => code !== '')
        },
        isPasswordStrong() {
            return /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d\W_]{8,}$/.test(this.formData.password)
        },
        isStep3Valid() {
            return this.isPasswordStrong && this.formData.password === this.formData.rePassword
        },
        step3ErrorMsg() {
            if (!this.isPasswordStrong) {
                return 'Password must be at least 8 characters and include both letters and numbers.'
            }
            if (this.formData.password !== this.formData.rePassword) {
                return 'Passwords do not match. Please re-enter them.'
            }
            return ''
        }
    },
    methods: {
        handleBack() {
            if (this.currentStep > 1 && this.currentStep < 4) {
                this.currentStep--
                return
            }
            this.$router.push('/signin')
        },
        showToast(message, type = 'info') {
            this.toast.message = message
            this.toast.type = type
            this.toast.visible = true
        },
        buildIdentityPayload() {
            const payload = {
                identityType: this.activeTab === 'email' ? 'EMAIL' : 'PHONE'
            }

            if (this.activeTab === 'email') {
                payload.email = this.formData.email
            } else {
                payload.phone = this.formData.phone
                payload.phoneCountryCode = this.selectedPhoneAreaCode.code
            }

            return payload
        },
        resolveVerifyTicket(data) {
            return data?.data?.verifyTicket || data?.verifyTicket || ''
        },
        async sendResetCode() {
            if (!this.isStep1Valid || this.isLoading) {
                this.errors.step1 = true
                return
            }

            this.errors.step1 = false
            this.isLoading = true
            try {
                const response = await fetch(buildApiUrl('/sso/auth-code/send'), {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        ...this.buildIdentityPayload(),
                        scene: AUTH_CODE_SCENE_RESET_PASSWORD
                    })
                })
                const data = await response.json()

                if (response.ok) {
                    this.showToast(data.message || 'Verification code sent successfully', 'success')
                    this.currentStep = 2
                    this.focusFirstCodeInput()
                } else {
                    this.showToast(data.message || 'Failed to send verification code', 'error')
                }
            } catch (error) {
                console.error('Send reset code error:', error)
                this.showToast('Network error, please try again later', 'error')
            } finally {
                this.isLoading = false
            }
        },
        async verifyResetCode() {
            if (!this.isStep2Valid || this.isLoading) {
                this.errors.step2 = true
                return
            }

            this.errors.step2 = false
            this.isLoading = true
            try {
                const response = await fetch(buildApiUrl('/sso/auth-code/verify'), {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        ...this.buildIdentityPayload(),
                        authCode: this.formData.verificationCode.join(''),
                        scene: AUTH_CODE_SCENE_RESET_PASSWORD
                    })
                })
                const data = await response.json()

                if (response.ok) {
                    this.verifyTicket = this.resolveVerifyTicket(data)
                    if (!this.verifyTicket) {
                        this.errors.step2 = true
                        this.showToast('Verification succeeded but verifyTicket is missing.', 'error')
                        return
                    }
                    this.currentStep = 3
                } else {
                    this.errors.step2 = true
                    this.showToast(data.message || 'Incorrect code. Please check and try again.', 'error')
                }
            } catch (error) {
                console.error('Verify reset code error:', error)
                this.errors.step2 = true
                this.showToast('Network error, please try again later', 'error')
            } finally {
                this.isLoading = false
            }
        },
        async resetPassword() {
            if (!this.isStep3Valid || this.isLoading) {
                this.errors.step3 = true
                return
            }
            if (!this.verifyTicket) {
                this.showToast('Please complete verification before resetting your password.', 'error')
                return
            }

            this.errors.step3 = false
            this.isLoading = true
            try {
                const response = await fetch(buildApiUrl('/sso/password/reset'), {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        ...this.buildIdentityPayload(),
                        newPassword: this.formData.password,
                        verifyTicket: this.verifyTicket
                    })
                })
                const data = await response.json()

                if (response.ok) {
                    this.currentStep = 4
                } else {
                    this.showToast(data.message || 'Failed to reset password', 'error')
                }
            } catch (error) {
                console.error('Reset password error:', error)
                this.showToast('Network error, please try again later', 'error')
            } finally {
                this.isLoading = false
            }
        },
        focusFirstCodeInput() {
            this.$nextTick(() => {
                if (this.$refs.codeInputs && this.$refs.codeInputs.length > 0) {
                    this.$refs.codeInputs[0].focus()
                }
            })
        },
        onCodeInput(event, index) {
            const value = event.target.value.replace(/[^0-9]/g, '')
            const singleChar = value.charAt(value.length - 1) || ''
            this.$set(this.formData.verificationCode, index, singleChar)
            this.errors.step2 = false
            if (singleChar && index < 5) {
                this.$nextTick(() => {
                    this.$refs.codeInputs[index + 1].focus()
                })
            }
        },
        onCodeDelete(event, index) {
            if (!this.formData.verificationCode[index] && index > 0) {
                this.$nextTick(() => {
                    this.$refs.codeInputs[index - 1].focus()
                })
            }
        },
        onCodePaste(event) {
            event.preventDefault()
            const numbers = (event.clipboardData || window.clipboardData)
                .getData('text')
                .replace(/[^0-9]/g, '')
                .slice(0, 6)
                .split('')

            for (let i = 0; i < numbers.length; i++) {
                this.$set(this.formData.verificationCode, i, numbers[i])
            }

            if (numbers.length > 0) {
                const focusIndex = Math.min(numbers.length, 5)
                this.$nextTick(() => {
                    this.$refs.codeInputs[focusIndex].focus()
                })
            }
        }
    }
}
</script>

<style scoped>
.reset-page {
    min-height: 100vh;
    background: #fff;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
    color: #252733;
}

.top-nav {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
}

.back-btn {
    display: flex;
    align-items: center;
    color: #ff466a;
    font-size: 16px;
    font-weight: 500;
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
}

.signin-top-btn {
    background: linear-gradient(90deg, #ff466a 0%, #ff5b87 100%);
    color: #fff;
    border: none;
    border-radius: 10px;
    padding: 10px 18px;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
}

.reset-container,
.success-container {
    max-width: 390px;
    margin: 0 auto;
    padding: 60px 16px 48px;
    display: flex;
    flex-direction: column;
    align-items: center;
}

.icon-wrap {
    height: 120px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.lock-icon {
    position: relative;
    width: 64px;
    height: 78px;
}

.lock-shackle {
    position: absolute;
    left: 18px;
    top: 0;
    width: 28px;
    height: 30px;
    border: 6px solid #e3e5eb;
    border-bottom: none;
    border-radius: 18px 18px 0 0;
}

.lock-body {
    position: absolute;
    left: 12px;
    top: 24px;
    width: 40px;
    height: 38px;
    background: linear-gradient(180deg, #ff4b74 0%, #ff466a 100%);
    border-radius: 6px;
}

.keyhole {
    position: absolute;
    left: 17px;
    top: 8px;
    width: 6px;
    height: 14px;
    background: #fff;
    border-radius: 3px;
}

.keyhole::before {
    content: '';
    position: absolute;
    left: -4px;
    top: -6px;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: #fff;
}

.badge {
    position: absolute;
    right: -10px;
    bottom: -6px;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: #ff7d73;
    color: #fff;
    font-size: 16px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
}

.step-title {
    margin: 8px 0 14px;
    font-size: 22px;
    font-weight: 800;
    text-align: center;
    color: #252733;
}

.step-subtitle {
    margin: 0 0 28px;
    font-size: 16px;
    line-height: 1.5;
    text-align: center;
    color: #8e93a3;
}

.tabs {
    width: 100%;
    display: flex;
    margin-bottom: 16px;
    border-bottom: 1px solid #d9dce3;
}

.tab {
    flex: 1;
    text-align: center;
    padding: 0 0 12px;
    font-size: 16px;
    color: #b0b4bf;
    font-weight: 600;
    cursor: pointer;
    position: relative;
}

.tab.active {
    color: #5a667a;
}

.tab.active::after {
    content: '';
    position: absolute;
    left: 0;
    bottom: -1px;
    width: 100%;
    height: 2px;
    background: #252733;
}

.input-container {
    width: 100%;
    height: 54px;
    border: 1px solid #dfe2e8;
    border-radius: 12px;
    background: #fff;
    display: flex;
    align-items: center;
    padding: 0 14px;
    transition: border-color 0.2s;
}

.input-container input {
    width: 100%;
    border: none;
    outline: none;
    font-size: 15px;
    color: #252733;
    background: transparent;
}

.input-container input::placeholder {
    color: #c8ccd5;
}

.input-container.error-border,
.phone-selector-box.error-border,
.phone-input-box.error-border,
.code-box.error-border {
    border-color: #ff5a5f;
}

.input-icon {
    margin-right: 10px;
    flex-shrink: 0;
}

.phone-row {
    width: 100%;
    display: flex;
    gap: 10px;
}

.phone-selector-box {
    width: 110px;
    min-width: 110px;
    height: 54px;
    border: 1px solid #dfe2e8;
    border-radius: 12px;
    display: flex;
    align-items: center;
    padding: 0 10px;
}

.phone-input-box {
    flex: 1;
}

.helper-text {
    width: 100%;
    margin-top: 8px;
    font-size: 12px;
    color: #8e93a3;
}

.error-message {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 14px;
    color: #ff5a5f;
    font-size: 13px;
}

.error-inline {
    margin-bottom: 26px;
}

.primary-btn {
    width: 100%;
    height: 54px;
    margin-top: 34px;
    border: none;
    border-radius: 12px;
    background: #eceef2;
    color: #fff;
    font-size: 16px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
}

.primary-btn.active {
    /* background: linear-gradient(90deg, #ff466a 0%, #ffa154 100%); */
    background: linear-gradient(270deg, rgba(255, 158, 79, 1) 0%, rgba(255, 66, 107, 1) 100%), linear-gradient(180deg, rgba(255, 158, 79, 1) 0%, rgba(255, 66, 107, 1) 100%), rgba(255, 70, 106, 1);
    box-shadow: 0px 1px 2px rgba(105, 81, 255, 0.05);
}

.primary-btn:disabled {
    cursor: not-allowed;
}

.code-inputs {
    width: 100%;
    display: flex;
    gap: 8px;
    margin-top: 12px;
}

.code-box {
    width: 100%;
    max-width: 52px;
    height: 54px;
    border: 1px solid #dfe2e8;
    border-radius: 10px;
    text-align: center;
    font-size: 28px;
    font-weight: 700;
    color: #252733;
    outline: none;
}

.mb-4 {
    margin-bottom: 16px;
}

.eye-icon {
    width: 22px;
    height: 22px;
    cursor: pointer;
    opacity: 0.65;
    flex-shrink: 0;
}

.success-icon {
    margin-top: 120px;
    margin-bottom: 12px;
}

.envelope {
    position: relative;
    width: 68px;
    height: 48px;
    background: linear-gradient(135deg, #ffb262 0%, #ff9852 100%);
    border-radius: 10px;
}

.envelope::before,
.envelope::after {
    content: '';
    position: absolute;
    top: 18px;
    width: 34px;
    height: 24px;
    background: #ffd4ad;
}

.envelope::before {
    left: 0;
    clip-path: polygon(0 0, 100% 100%, 0 100%);
}

.envelope::after {
    right: 0;
    clip-path: polygon(100% 0, 100% 100%, 0 100%);
}

.envelope-check {
    position: absolute;
    left: 20px;
    top: -16px;
    width: 28px;
    height: 28px;
    background: #f2f5f9;
    border-radius: 6px;
}

.envelope-check::before {
    content: '';
    position: absolute;
    left: 8px;
    top: 6px;
    width: 12px;
    height: 7px;
    border-left: 4px solid #21c47b;
    border-bottom: 4px solid #21c47b;
    transform: rotate(-45deg);
}

.success-btn {
    margin-top: 80px;
}

.loader {
    border: 3px solid rgba(255, 255, 255, 0.35);
    border-top: 3px solid #fff;
    border-radius: 50%;
    width: 22px;
    height: 22px;
    animation: spin 1s linear infinite;
}

.mr-1 {
    margin-right: 4px;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }
    100% {
        transform: rotate(360deg);
    }
}
</style>
